import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import PostControls from 'flarum/forum/utils/PostControls';
import Button from 'flarum/common/components/Button';
import Modal from 'flarum/common/components/Modal';

/* SP-20 (Homepage Excerpt), ruling of 2026-10-29: an admin picks which post the Home Page reveals, and for how long.
   Admins see "Featured Post" in a post's own menu (operator, 2026-10-30: the menu item, dialog title and button read "Featured Post"); the dialog shows what the Home Page reveals now, ends a
   pick early, and sets this post for a length or until a date. The server (HomeRevealController) checks the admin
   permission and that the post is public. Without a pick the Home Page makes its automatic choice. */
const LENGTHS = [['1d', '1 day'], ['3d', '3 days'], ['1w', '1 week'], ['2w', '2 weeks'], ['1m', '1 month'], ['date', 'Until a chosen date']];

function endText(until) {
  return new Date(until * 1000).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// 2026-10-30: an image-only post can be picked; the Home Page then shows its title, image and byline, with no text.
function imageOnly(post) {
  const doc = new DOMParser().parseFromString(post.contentHtml() || '', 'text/html');
  return doc.body.textContent.trim() === '' && !!doc.querySelector('img');
}

class HomeRevealModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.post = this.attrs.post;
    this.length = '1w';
    this.date = '';
    this.current = undefined;   // undefined while loading, then {pick: null | {...}}
    this.error = '';
    app.request({ method: 'GET', url: app.forum.attribute('apiUrl') + '/citreg/home-reveal' })
      .then((r) => { this.current = r; m.redraw(); })
      .catch(() => { this.current = { pick: null }; this.error = 'Could not read what the Home Page reveals now.'; m.redraw(); });
  }

  className() { return 'CitRegHomeRevealModal Modal--small'; }

  title() { return 'Featured Post'; }

  content() {
    const pick = this.current && this.current.pick;
    const minDate = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
    return (
      <div className="Modal-body">
        <div className="Form-group CitRegHomeReveal-now">
          <label>Now on the Home Page</label>
          {this.current === undefined ? <p>…</p> : pick ? (
            <div>
              <p>
                <a href={app.route('discussion.near', { id: pick.discussionId, near: pick.postNumber })}>{pick.title || 'Post ' + pick.postId}</a>
                {' (post ' + pick.postNumber + '), until ' + endText(pick.until)}
                {pick.setBy ? ', set by ' + pick.setBy : ''}
                {pick.eligible ? '' : '. This post is no longer public, so the Home Page shows the automatic choice.'}
              </p>
              <Button className="Button" loading={this.loading} onclick={() => this.send('DELETE')}>End now (return to automatic)</Button>
            </div>
          ) : <p>The automatic choice: the newest public post with at least 300 characters of text, or 80 with an uploaded image.</p>}
        </div>
        <div className="Form-group">
          <label>Reveal this post ({(this.post.discussion() && this.post.discussion().title()) || ''}, post {this.post.number()}) for</label>
          {LENGTHS.map(([value, label]) => (
            <label className="checkbox" style="display:block;font-weight:normal">
              <input type="radio" name="citreg-reveal-length" value={value} checked={this.length === value} onchange={() => { this.length = value; }} /> {label}
            </label>
          ))}
          {imageOnly(this.post) ? <p className="helpText">This post has no text. The featured excerpt will show the discussion title, image and author.</p> : null}
          {this.length === 'date' ? (
            <input className="FormControl" type="date" min={minDate} value={this.date} oninput={(e) => { this.date = e.target.value; }} style="margin-top:6px" />
          ) : null}
        </div>
        {this.error ? <p className="CitRegHomeReveal-error" style="color:#a6192e">{this.error}</p> : null}
        <div className="Form-group">
          <Button className="Button Button--primary" loading={this.loading} disabled={this.length === 'date' && !this.date}
            onclick={() => this.send('POST')}>Featured Post</Button>
        </div>
      </div>
    );
  }

  send(method) {
    this.loading = true;
    this.error = '';
    const body = method === 'POST'
      ? Object.assign({ postId: Number(this.post.id()) }, this.length === 'date' ? { until: this.date } : { duration: this.length })
      : undefined;
    app.request({ method, url: app.forum.attribute('apiUrl') + '/citreg/home-reveal', body, errorHandler: () => {} })
      .then((r) => { this.current = r; this.loading = false; m.redraw(); })
      .catch((e) => {
        this.loading = false;
        this.error = (e && e.response && e.response.error) || 'That did not work. Try again, or reload the page.';
        m.redraw();
      });
  }
}

export default function installHomeReveal() {
  extend(PostControls, 'moderationControls', function (items, post) {
    const user = app.session.user;
    if (!user || !user.isAdmin() || post.contentType() !== 'comment' || post.isHidden()) return;
    items.add('citregHomeReveal', (
      <Button icon="fas fa-home" onclick={() => app.modal.show(HomeRevealModal, { post })}>Featured Post</Button>
    ), -50);
  });
}
